import { StrandTimeSeriesData } from "@/pages/caster/models/StrandSpeed";
import {
	CasterProcessData,
	Probe,
} from "@/pages/heat-report/models/HeatReportModels";
import { getMomentTz } from "@dexteel/mesf-core";
import { EChartsOption } from "echarts-for-react";
import { useMemo } from "react";

// Color constants
const COLORS = {
	LUST_RED: "#E41A1C", // Red for S1
	STEEL_BLUE: "#377EB8", // Blue for S2
	APPLE_GREEN: "#4DAF4A", // Green for S3
	MIDDLE_BLUE_PURPLE: "#9467bd", // Purple for Probe Temp
	ORANGE: "#ff7f00", // Orange (unused)
	LILAC_PURPLE: "#c4aed1", // Light purple (unused)
} as const;

const moment = getMomentTz();

interface UseChartOptionsProps {
	strandSpeeds: StrandTimeSeriesData[] | null | undefined;
	probes?: Probe[] | null;
	heatName: string;
}

const strandColors: { [key: string]: string } = {
	S1: COLORS.LUST_RED, // Red for S1
	S2: COLORS.STEEL_BLUE, // Blue for S2
	S3: COLORS.APPLE_GREEN, // Green for S3
	Probe_Temp: COLORS.MIDDLE_BLUE_PURPLE, // Purple for Probe Temp
};

// Define which axis each series should use
const axisMapping: { [key: string]: number } = {
	strand_speed: 0, // Strand speeds use axis 0 (IPM)
	probe_temp: 1, // Probe temp uses axis 1 (°F)
};

export const useChartOptions = ({
	strandSpeeds,
	probes,
	heatName,
}: UseChartOptionsProps): EChartsOption => {
	const chartOptions = useMemo<EChartsOption>(() => {
		const data: StrandTimeSeriesData[] = (strandSpeeds || [])
			.map((temp) => ({
				Datetime: temp.Datetime,
				Value: temp.Value ?? 0,
				TagId: temp.TagId?.toString() || "",
				TagName: temp.TagName || "",
			}))
			.filter((item) => item.Datetime && item.TagId && item.TagName);

		if (data.length === 0) {
			return {
				title: {
					left: "center",
					top: "center",
				},
				series: [],
			};
		}

		const values = data.map((item) => item.Value);
		const minValue = Math.min(...values).toFixed(0);
		const maxValue = Math.max(...values).toFixed(0);

		// Calculate y-axis limits
		const margin = Math.ceil((Number(maxValue) - Number(minValue)) * 0.05);
		let yMin = Math.max(0, Math.floor(Number(minValue)) - margin);
		let yMax = Math.ceil(Number(maxValue) + margin);

		// Filter probes that are within the chart time range
		const chartTimeRange = {
			start: Math.min(...data.map((item) => item.Datetime.getTime())),
			end: Math.max(...data.map((item) => item.Datetime.getTime())),
		};

		const filteredProbes = (probes || []).filter((probe) => {
			const probeTime = probe.Timestamp.getTime();
			return (
				probeTime >= chartTimeRange.start && probeTime <= chartTimeRange.end
			);
		});

		// Fixed temperature axis limits with band system (same as EAF chart)
		const tempAxisLimits = { min: 2400, max: 3600, interval: 240 };

		// Calculate quantity of intervals for the y-axis
		const interval = Math.ceil((yMax - yMin) / 5);

		const groupedData = data.reduce<{ [key: string]: StrandTimeSeriesData[] }>(
			(acc, item) => {
				if (!acc[item.TagId]) {
					acc[item.TagId] = [];
				}
				acc[item.TagId].push(item);
				return acc;
			},
			{},
		);

		const tagColorMap: { [tag: string]: string } = {};
		let colorIndex = 0;
		Object.entries(groupedData).forEach(([tagId, tagData]) => {
			// Assign color based on TagName prefix
			const tagName = tagData[0]?.TagName || "";
			const prefix = tagName.substring(0, 2); // Get the first two characters (S1, S2, S3)
			if (strandColors[prefix]) {
				tagColorMap[tagId] = strandColors[prefix];
			} else {
				// Fallback for unexpected TagNames
				const fallbackColors = [
					COLORS.LUST_RED,
					COLORS.STEEL_BLUE,
					COLORS.APPLE_GREEN,
					COLORS.MIDDLE_BLUE_PURPLE,
					COLORS.ORANGE,
				];
				tagColorMap[tagId] = fallbackColors[colorIndex % fallbackColors.length];
				colorIndex++;
			}
		});

		return {
			animation: false,
			toolbox: {
				feature: {
					saveAsImage: {
						name: `Heat ${heatName} - Strand Speeds`,
						pixelRatio: 2,
					},
				},
			},
			dataZoom: [
				{
					type: "inside",
					show: true,
				},
			],
			tooltip: {
				trigger: "axis",
				formatter: (params: any) => {
					const datetime = moment(params[0].value[0]).format(
						"yyyy-MM-DD HH:mm:ss",
					);

					const tooltipContent = params
						.map(
							(param: any) =>
								`${param.seriesName}: ${param.value[1].toFixed(1)}`,
						)
						.join("<br>");

					return `Time: ${datetime}<br>${tooltipContent}`;
				},
			},
			legend: {
				data: [
					...Object.keys(groupedData)
						.map((tagId) => groupedData[tagId][0].TagName)
						.sort(),
					...(filteredProbes.length > 0 ? ["Probe Temp"] : []),
				],
				top: "bottom",
				selectedMode: true,
				textStyle: {
					fontSize: 10,
				},
				itemGap: 20,
			},
			xAxis: {
				type: "time",
				axisLabel: {
					formatter: (value: number) => {
						return moment(value).format("HH:mm");
					},
				},
			},
			yAxis: [
				// Y-axis 0: Strand Speed (IPM)
				{
					type: "value",
					name: "IPM",
					nameTextStyle: {
						fontSize: 14,
						align: "left",
					},
					nameLocation: "end",
					position: "left",
					min: yMin,
					max: yMax,
					interval: interval,
				},
				// Y-axis 1: Probe Temperature (°F)
				...(filteredProbes.length > 0
					? [
							{
								type: "value",
								name: "°F",
								nameTextStyle: {
									fontSize: 14,
									color: COLORS.MIDDLE_BLUE_PURPLE,
									align: "right",
								},
								nameLocation: "end",
								position: "right",
								axisLabel: {
									color: COLORS.MIDDLE_BLUE_PURPLE,
								},
								axisLine: {
									lineStyle: {
										color: COLORS.MIDDLE_BLUE_PURPLE,
									},
								},
								...tempAxisLimits,
							},
						]
					: []),
			],
			series: [
				// Strand speed series
				...Object.entries(groupedData).map(([tagId, tagData]) => ({
					name: tagData[0].TagName,
					type: "line",
					smooth: true,
					symbol: "none",
					sampling: "lttb",
					yAxisIndex: axisMapping.strand_speed,
					itemStyle: {
						color: tagColorMap[tagId],
					},
					data: tagData.map((item) => [item.Datetime.getTime(), item.Value]),
				})),
				// Probe temperatures series (only if there are probes in the time range)
				...(filteredProbes.length > 0
					? [
							{
								name: "Probe Temp",
								type: "scatter",
								yAxisIndex: axisMapping.probe_temp,
								symbol: "circle",
								symbolSize: 8,
								itemStyle: {
									color: COLORS.MIDDLE_BLUE_PURPLE, // Purple color for probes (consistent with EAF chart)
									borderColor: "#fff",
									borderWidth: 2,
								},
								label: {
									show: true,
									position: "top",
									formatter: (params: any) => {
										const probe = filteredProbes.find(
											(p) => p.Timestamp.getTime() === params.value[0],
										);
										if (probe) {
											return `${probe.Temperature.toFixed(0)}°F`;
										}
										return "";
									},
									fontSize: 11,
									fontWeight: "bold",
									color: COLORS.MIDDLE_BLUE_PURPLE,
								},
								emphasis: {
									itemStyle: {
										shadowBlur: 15,
										shadowColor: COLORS.MIDDLE_BLUE_PURPLE,
									},
								},
								data: filteredProbes.map((probe) => [
									probe.Timestamp.getTime(),
									probe.Temperature, // Use actual temperature value
								]),
							},
						]
					: []),
			],
		};
	}, [strandSpeeds, probes, heatName]);

	return chartOptions;
};
